import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import fill from 'lodash/fill';
import chunk from 'lodash/chunk';
import isEmpty from 'lodash/isEmpty';
import FocusLock from 'react-focus-lock';
import { mediaTypes, scrollSize } from './constants';
import Button from '../Button';
import Icon from '../Icon';
import Image from '../Image';
import './styles.css';

export default class MediaCarousel extends React.Component {
  static propTypes = {
    media: PropTypes.arrayOf(PropTypes.object),
    isEditing: PropTypes.bool,
    setTileSelected: PropTypes.func,
    resetModalState: PropTypes.func,
    tileSelected: PropTypes.object,
    fileDataStr: PropTypes.string,
    centerImage: PropTypes.number,
    enabledFeatures: PropTypes.arrayOf(PropTypes.string),
  };

  state = {
    isRightButtonVisible: true,
    isLeftButtonVisible: false,
    openModal: false,
  }

  constructor(props) {
    super(props);
    const ids = [];
    for (let i = 1; i <= 31; i += 1) {
      ids[i] = i.toString();
    }
    this.state.ids = ids;
    this.state.centeredImage = 0;
  }

  componentWillReceiveProps = (nextProps) => {
    const { centerImage = 0 } = this.props;
    const { centeredImage = 0 } = this.state;
    if (centerImage !== centeredImage && centerImage > 1) {
      setTimeout(() => {
        this.centerImage(centerImage);
      });
    }
  };

  slideWrapperLeft = () => {
    if (this.imagesWrapperRef) {
      const { scrollLeft, scrollWidth, clientWidth } = this.imagesWrapperRef;
      const maxScroll = scrollWidth - clientWidth;
      let left = 0;
      if (scrollLeft + scrollSize > maxScroll) {
        left = maxScroll - scrollLeft;
        this.setState({ isRightButtonVisible: false });
      } else {
        this.setState({ isLeftButtonVisible: true });
        left = scrollSize;
      }
      this.imagesWrapperRef.scroll({
        top: 0,
        left: scrollLeft + left,
        behavior: 'smooth',
      });
      this.fixChromeScrolling(scrollLeft, scrollLeft + left);
    }
  };

  slideWrapperRight = () => {
    if (this.imagesWrapperRef) {
      const { scrollLeft } = this.imagesWrapperRef;
      let left = 0;
      if (scrollLeft - scrollSize < 0) {
        left = scrollLeft;
        this.setState({ isLeftButtonVisible: false });
      } else {
        this.setState({ isRightButtonVisible: true });
        left = scrollSize;
      }
      this.imagesWrapperRef.scroll({
        top: 0,
        left: scrollLeft - left,
        behavior: 'smooth',
      });
      this.fixChromeScrolling(scrollLeft, scrollLeft - left);
    }
  };

  /* istanbul ignore next */
  fixChromeScrolling = (oldScrollLeft, newScrollLeft) => {
    // Fix for Chrome bug https://issues.chromium.org/issues/376097633
    if (this.props.enabledFeatures?.includes('feature.match.hubs.fixWebtourScrollingOnChrome')) {
      setTimeout(() => {
        if (this.imagesWrapperRef.scrollLeft === oldScrollLeft) {
          this.imagesWrapperRef.scrollLeft = newScrollLeft;
        }
      }, 550);
    }
  };

  centerImage = (index) => {
    /* istanbul ignore else */
    if (this.imagesWrapperRef) {
      const { scrollLeft, scrollWidth, clientWidth } = this.imagesWrapperRef;
      const totalWidth = scrollSize * Math.floor(index/2 + 1);
      const maxScroll = scrollWidth - clientWidth;

      this.setState({
        isLeftButtonVisible: (index > 2),
        isRightButtonVisible: !(scrollLeft + totalWidth > maxScroll),
        centeredImage: index,
      });

      this.imagesWrapperRef.scroll({
        top: 0,
        left: totalWidth,
        behavior: 'auto',
      });
    } else {
      this.setState({ centeredImage: index });
    }
  };

  transformUrl = (url) => {
    const secureUrl = url.replace('http://', 'https://');
    if (secureUrl.includes('youtube')) {
      const qsParams = url.split(/[?&#]/);
      let videoId = '';
      // skip the url itself
      for (let i = 1; i < qsParams.length; i+=1) {
        const parts = qsParams[i].split('=');
        if (parts[0] === 'v') {
          videoId = parts[1];
          break;
        }
      }
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (secureUrl.includes('vimeo')) return secureUrl.replace('vimeo.com', 'player.vimeo.com/video');
    return secureUrl;
  };

  isValidMedia = (mediaElement) => {
    let isValid = !!mediaElement && !!mediaElement.media_type &&
      (mediaElement.media_type === mediaTypes.IMAGE_URL ||
        mediaElement.media_type === mediaTypes.IMAGE_S3 ||
        mediaElement.media_type === mediaTypes.VIDEO_URL);

    if (isValid && mediaElement.media_type === mediaTypes.IMAGE_S3) {
      isValid = !!mediaElement.mediaDisplayUrl;
    }

    return isValid;
  }

  /* eslint-disable camelcase */
  mapMediaElement = (mediaElement, imageStyleName = 'image-content') => {
    const { isEditing } = this.props;

    if (!this.isValidMedia(mediaElement)) return <div styleName="mock-image" />;
    switch (mediaElement.media_type) {
      case mediaTypes.IMAGE_URL:
        return (<div styleName={imageStyleName} style={{ backgroundImage: `url("${encodeURI(mediaElement.media_url)}")` }} />);
      case mediaTypes.VIDEO_URL:
        return (
          <iframe
            styleName={isEditing ? 'video-iframe-disabled' : 'video-iframe'}
            title={mediaElement.title}
            src={this.transformUrl(mediaElement.media_url)}
            name={mediaElement.title}
            frameBorder="0"
          />
        );
      case mediaTypes.IMAGE_S3: {
        const imageData = mediaElement.file || mediaElement.mediaDisplayUrl;
        return (<div styleName={imageStyleName} style={{ backgroundImage: `url("${encodeURI(imageData)}")` }} data-cy="Premium-Hem-Media-Small-Photo" />);
      }
      default:
        return (<div styleName="mock-image" />);
    }
  };
  /* eslint-enable camelcase */

  mapMediaChunk = (mediaChunk, index) => {
    const { isEditing, tileSelected, fileDataStr, resetModalState } = this.props;
    const { ids } = this.state;

    /* eslint-disable jsx-a11y/click-events-have-key-events */

    const selectedTileOverlay = (displayPhoto) => (
      <button
        type="button"
        styleName={displayPhoto ? 'selected-image-with-border small-image' : 'selected-image small-image'}
        onClick={resetModalState}
      >
        {displayPhoto ? <Image src={displayPhoto} alt="file" styleName="image-content" /> :
        <Icon name="check circle" styleName="selected-image-checkbox" />}
      </button>
    );

    const smallImage = (chunkPiece, offset) => {
      const id = ids[(index) * 2 + offset];
      const chunkPieceFile = get(chunkPiece, 'file');
      const displayPhoto = chunkPieceFile || fileDataStr;
      const mediaType = get(chunkPiece, 'media_type');
      const tileSelectedId = get(tileSelected, 'id');
      return (
        <div styleName="small-image-container" aria-label={`tile number ${+id + 1}`} data-cy="Premium-Hem-Media-Small-Tile">
          {(isEditing && tileSelectedId === id && !mediaType) ?
            selectedTileOverlay(displayPhoto) : (
              <div
                name="media-carousel-small-photo"
                role="button"
                tabIndex={0}
                styleName={tileSelectedId === id ? 'selected-image-with-border small-image' : 'small-image'}
                id={id}
                onClick={(e) => this.handleTileClick(e, chunkPiece, id)}
                onKeyPress={this.onKeyPress}
              >
                {(displayPhoto && tileSelectedId === id) ?
                  <Image src={displayPhoto} alt="file" styleName="image-content" /> :
                  this.mapMediaElement(chunkPiece)}
              </div>
            )}
          <div
            id={`id-tile-number-${+id + 1}`}
            styleName={(tileSelectedId === id && displayPhoto) || this.isValidMedia(chunkPiece) ? 'tile-number' : 'tile-number empty-tile'}
          >{+id + 1}
          </div>
        </div>
      );
    };

    return (
      <div key={index} styleName="small-images-container">
        {smallImage(mediaChunk[0], 1)}
        {smallImage(mediaChunk[1], 2)}
      </div>
    );
  }

  handleTileClick = (e, mediaTile, targetIndex) => {
    // when is not Editing mode we need to avoid click logic
    if (!this.props.isEditing) {
      this.setState({ openModal: true, selectedTile: { id: Number(targetIndex) } });
      return;
    }

    const { setTileSelected, resetModalState, tileSelected } = this.props;
    const tileSelectedId = get(tileSelected, 'id');
    const targetString = targetIndex.toString();

    if (isEmpty(get(mediaTile, 'media_url'))) {
      setTileSelected({ id: e.currentTarget.id });
    } else if (tileSelectedId === targetString) {
      resetModalState();
    } else {
      setTileSelected({ id: e.currentTarget.id, ...mediaTile });
    }
  }

  onKeyPress = (event) => {
    if (!this.props.isEditing && event.key === 'Enter') {
      this.setState({ openModal: true, selectedTile: { id: event.target.id } });
    }
    event.preventDefault();
  };

  onKeyPressModal = (event) => {
    /* istanbul ignore else */
    if (event.key === 'Enter') {
      const { className } = event.target;
      if (className.includes('close')) {
        this.setState({ openModal: false });
      } else if (className.includes('left')) {
        this.previousImage();
      } else if (className.includes('right')) {
        this.nextImage();
      }
      event.preventDefault();
    }
  };

  handleImagesWrapperRef = (el) => { this.imagesWrapperRef = el; };

  closeModal = () => {
    this.setState({ openModal: false });
  }

  previousImage = () => {
    const { selectedTile } = this.state;
    const id = selectedTile && selectedTile.id ? String(Number(selectedTile.id) - 1) : '0';
    this.setState({ openModal: true, selectedTile: { id } });
  }

  nextImage = () => {
    const { selectedTile } = this.state;
    const id = selectedTile && selectedTile.id ? String(Number(selectedTile.id) + 1) : '0';
    this.setState({ openModal: true, selectedTile: { id } });
  }

  render() {
    const { media=[], isEditing, resetModalState, tileSelected, fileDataStr } = this.props;
    const { isRightButtonVisible, isLeftButtonVisible, selectedTile, openModal } = this.state;
    const chunks = chunk(media.slice(1), 2);

    const selectedBigTileOverlay = (
      <button
        id="big-image-wrapper"
        type="button"
        styleName={fileDataStr ? 'selected-image-with-border big-image-wrapper' : 'selected-image big-image-wrapper'}
        onClick={resetModalState}
      >
        {fileDataStr ? <Image src={fileDataStr} alt="file" styleName="image-content" /> :
        <Icon name="check circle" styleName="selected-image-checkbox" />}
      </button>
    );

    const tileSelectedId = get(tileSelected, 'id');
    const selectedTileId = selectedTile && selectedTile.id ? Number(selectedTile.id) : 0;

    return (
      <div styleName="images-overlay">
        <div styleName="images-wrapper" ref={this.handleImagesWrapperRef}>
          <div styleName="big-image-container" aria-label="tile number 1" data-cy="Premium-Hem-Media-Big-Tile">
            {isEditing && (tileSelectedId === '0') && !get(media[0], 'media_type') ?
              selectedBigTileOverlay :
              (
                <div
                  name="media-carousel-small-photo"
                  role="button"
                  tabIndex={0}
                  styleName={tileSelectedId === '0' ?
                    'selected-image-with-border big-image-wrapper' :
                    'big-image-wrapper'}
                  id="0"
                  onKeyPress={this.onKeyPress}
                  onClick={(e) => this.handleTileClick(e, media[0], 0)}
                >
                  {fileDataStr && tileSelectedId === '0' ?
                    <Image src={fileDataStr} alt="file" styleName="image-content" /> :
                    this.mapMediaElement(media[0])}
                </div>
              )}
            <div
              id="id-tile-number-1"
              styleName={(tileSelectedId === '0' && fileDataStr) || this.isValidMedia(media[0]) ? 'tile-number' : 'tile-number empty-tile'}
            >1
            </div>
          </div>
          {chunks.concat(fill(Array(15 - chunks.length), [])).map(this.mapMediaChunk)}
        </div>
        {isRightButtonVisible &&
        (<Button onClick={this.slideWrapperLeft} styleName="right-button" positive circular icon="arrow right" size="massive" />)}
        {isLeftButtonVisible &&
        (<Button onClick={this.slideWrapperRight} styleName="left-button" positive circular icon="arrow left" size="massive" />)}
        {openModal && (
        <FocusLock returnFocus autoFocus>
          <div styleName="modal-blur" />
          <div styleName="modal-lightbox">
            <div styleName="modal-close-button">
              <Icon
                name="close"
                onClick={this.closeModal}
                aria-label="Close"
                onKeyPress={this.onKeyPressModal}
                tabIndex="0"
                role="button"
              />
            </div>
            <div styleName="modal-inner">
              <div styleName="modal-inner-viewer">
                <div
                  name="media-carousel-small-photo-modal"
                  role="button"
                  styleName="modal-image"
                  id={selectedTileId}
                >
                  {this.mapMediaElement(media[selectedTileId], 'modal-image')}
                </div>
              </div>
              {selectedTileId > 0 && (
              <div styleName="modal-left-button">
                <Icon
                  name="angle left"
                  aria-label="Previous image or video"
                  onClick={this.previousImage}
                  onKeyPress={this.onKeyPressModal}
                  tabIndex="0"
                  role="button"
                />
              </div>
              )}
              {!isEmpty(media[selectedTileId + 1]) && (
              <div styleName="modal-right-button">
                <Icon
                  data-autofocus
                  name="angle right"
                  aria-label="Next image or video"
                  onClick={this.nextImage}
                  onKeyPress={this.onKeyPressModal}
                  tabIndex="0"
                  role="button"
                />
              </div>
              )}
              {media[selectedTileId] && media[selectedTileId].caption && (
              <div
                aria-label={media[selectedTileId].caption}
                styleName="caption-in-modal"
                tabIndex="-1"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: media[selectedTileId].caption }}
              />
              )}
            </div>
          </div>
        </FocusLock>
        )}
      </div>
    );
  }
}
/* eslint-enable jsx-a11y/click-events-have-key-events */
